import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    forwardRef,
    input,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Icon } from '../../icon/icon.component';

/**
 * Separadores aceitos ao digitar e ao colar: qualquer espaço em branco
 * (espaço, tabulação, quebra de linha), vírgula e ponto e vírgula.
 *
 * Sem a flag `g` de propósito — a expressão é usada tanto em `test()` quanto em
 * `split()`, e a flag manteria estado (`lastIndex`) entre as chamadas.
 */
const TAG_SEPARATORS = /[\s,;]+/;

/** Teclas que confirmam o texto digitado como uma tag. */
const SEPARATOR_KEYS: readonly string[] = ['Enter', ' ', ',', ';'];

/**
 * Campo de múltiplas tags (chips), integrável a formulários
 * (`ControlValueAccessor`).
 *
 * O usuário confirma uma tag pressionando **Enter, espaço, vírgula ou ponto e
 * vírgula**, ou **colando** um texto com vários itens separados por esses
 * mesmos caracteres — colar `"teste teste2 teste3"` cria três tags. Duplicatas
 * são ignoradas, tanto contra as tags existentes quanto dentro do mesmo lote
 * colado.
 *
 * Havendo ao menos uma tag (e não estando desabilitado), um botão à direita do
 * campo remove **todas** de uma vez.
 */
@Component({
    selector: 'ai-tag-input',
    templateUrl: './tag-input.component.html',
    styleUrls: ['./tag-input.component.scss'],
    imports: [Icon],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagInput),
            multi: true,
        },
    ],
})
export class TagInput implements ControlValueAccessor {
    /** Placeholder do campo de entrada. */
    readonly placeholder = input<string>('Adicionar tag...');

    /** Emite a lista atualizada de tags a cada inclusão/remoção. */
    readonly tagsChange = output<string[]>();

    /** Campo de texto interno (foco e limpeza do texto pendente). */
    private readonly inputEl =
        viewChild<ElementRef<HTMLInputElement>>('inputEl');

    /**
     * Estado interno das tags. Nunca é mutado no lugar: toda alteração define
     * uma **nova** lista (ver {@link writeValue}).
     */
    private readonly tagsState = signal<readonly string[]>([]);

    /** Tags atuais (somente leitura). */
    readonly tags = this.tagsState.asReadonly();

    /** Indica se há ao menos uma tag — condição para exibir o botão de limpar. */
    protected readonly hasTags = computed(() => this.tags().length > 0);

    /** Desabilitação vinda do formulário (via {@link setDisabledState}). */
    private readonly cvaDisabled = signal<boolean>(false);

    /** Indica se o componente está desabilitado. */
    readonly disabled = this.cvaDisabled.asReadonly();

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    // ─── ControlValueAccessor ────────────────────────────────────────────────

    /**
     * Recebe o valor do formulário.
     *
     * **Copia** a lista recebida — o componente jamais altera o array do
     * controle. Duplicatas são descartadas porque as tags são tratadas como um
     * conjunto (a remoção é por valor e o `@for` usa a própria tag como chave).
     *
     * @param value Lista de tags do formulário.
     */
    writeValue(value: string[] | null | undefined): void {
        this.tagsState.set(Array.isArray(value) ? [...new Set(value)] : []);
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Reflete o estado desabilitado do controle de formulário.
     *
     * @param isDisabled `true` quando o controle está desabilitado.
     */
    setDisabledState(isDisabled: boolean): void {
        this.cvaDisabled.set(isDisabled);
    }

    // ─── Entrada de tags ─────────────────────────────────────────────────────

    /**
     * Trata a digitação: as teclas separadoras (Enter, espaço, vírgula e ponto
     * e vírgula) confirmam o texto digitado como tag.
     *
     * A tecla separadora é **sempre** cancelada — inclusive com o campo vazio —
     * para que o caractere não entre no texto e para que o Enter não submeta o
     * formulário ao redor.
     *
     * @param event Evento de teclado do campo de texto.
     */
    addTagFromInput(event: KeyboardEvent): void {
        if (
            this.disabled() ||
            event.isComposing ||
            !SEPARATOR_KEYS.includes(event.key)
        ) {
            return;
        }
        event.preventDefault();

        const field = this.fieldOf(event);
        const value = (field?.value ?? '').trim();
        if (!value) {
            return;
        }
        this.addTags([value]);
        if (field) {
            field.value = '';
        }
    }

    /**
     * Trata a colagem: divide o conteúdo colado pelos separadores e transforma
     * cada trecho em uma tag.
     *
     * Quando o texto colado **não** contém separador, o evento não é
     * interceptado — o navegador o insere normalmente, permitindo colar um
     * trecho e continuar digitando.
     *
     * @param event Evento de colagem do campo de texto.
     */
    addTagsFromPaste(event: ClipboardEvent): void {
        if (this.disabled()) {
            return;
        }
        const pasted = event.clipboardData?.getData('text') ?? '';
        if (!pasted || !TAG_SEPARATORS.test(pasted)) {
            return;
        }
        event.preventDefault();

        const field = this.fieldOf(event);
        const merged = field ? this.mergeWithSelection(field, pasted) : pasted;
        const values = this.splitTags(merged);
        if (values.length === 0) {
            return;
        }
        this.addTags(values);
        if (field) {
            field.value = '';
        }
    }

    // ─── Remoção ─────────────────────────────────────────────────────────────

    /**
     * Remove a tag informada (busca **por valor**, coerente com o dedup).
     *
     * @param tag Tag a remover.
     */
    removeTag(tag: string): void {
        if (this.disabled()) {
            return;
        }
        const remaining = this.tags().filter((current) => current !== tag);
        if (remaining.length === this.tags().length) {
            return;
        }
        this.tagsState.set(remaining);
        this.onTouched();
        this.emitChanges();
    }

    /**
     * Remove **todas** as tags de uma vez e devolve o foco ao campo.
     *
     * @param event Evento de clique — a propagação é interrompida para não
     * acionar o clique do container (que refocaria o campo).
     */
    clearAll(event?: Event): void {
        event?.stopPropagation();
        if (this.disabled() || !this.hasTags()) {
            return;
        }
        this.tagsState.set([]);
        this.onTouched();
        this.emitChanges();
        this.focusInput();
    }

    // ─── Foco ────────────────────────────────────────────────────────────────

    /** Foca o campo de digitação (clique em qualquer área do container). */
    focusInput(): void {
        if (this.disabled()) {
            return;
        }
        this.inputEl()?.nativeElement.focus();
    }

    /** Marca o controle como "tocado" ao sair do campo. */
    onBlur(): void {
        this.onTouched();
    }

    // ─── Apoio ───────────────────────────────────────────────────────────────

    /**
     * Acrescenta as tags informadas, ignorando vazias e duplicadas — tanto
     * contra as já existentes quanto dentro do próprio lote.
     *
     * @param values Tags candidatas.
     */
    private addTags(values: string[]): void {
        const current = this.tags();
        const known = new Set(current);
        const added: string[] = [];

        for (const value of values) {
            if (!value || known.has(value)) {
                continue;
            }
            known.add(value);
            added.push(value);
        }

        if (added.length === 0) {
            return;
        }
        this.tagsState.set([...current, ...added]);
        this.emitChanges();
    }

    /**
     * Divide um texto pelos separadores, descartando trechos vazios.
     *
     * @param text Texto a dividir.
     * @returns Lista de tags normalizadas.
     */
    private splitTags(text: string): string[] {
        return text
            .split(TAG_SEPARATORS)
            .map((part) => part.trim())
            .filter((part) => part.length > 0);
    }

    /**
     * Reproduz o conteúdo que o campo teria após a colagem: o texto colado
     * substitui a seleção atual (ou é inserido no cursor), preservando o que já
     * havia sido digitado.
     *
     * @param field Campo de texto.
     * @param pasted Texto colado.
     * @returns Texto resultante.
     */
    private mergeWithSelection(
        field: HTMLInputElement,
        pasted: string,
    ): string {
        const value = field.value ?? '';
        const start = field.selectionStart ?? value.length;
        const end = field.selectionEnd ?? start;
        return value.slice(0, start) + pasted + value.slice(end);
    }

    /**
     * Campo de texto que originou o evento, com o `viewChild` como alternativa.
     *
     * @param event Evento originado no campo.
     * @returns O campo de texto, ou `null`.
     */
    private fieldOf(event: Event): HTMLInputElement | null {
        const target = event.target;
        return target instanceof HTMLInputElement
            ? target
            : (this.inputEl()?.nativeElement ?? null);
    }

    /** Notifica o formulário e os consumidores com cópias da lista atual. */
    private emitChanges(): void {
        const value = [...this.tags()];
        this.onChange(value);
        this.tagsChange.emit([...value]);
    }
}
